import { type CSSProperties, type ReactElement } from 'react';

import {
  colorAccessor,
  heightAccessor,
  widthAccessor,
  xAccessor,
  yAccessor,
} from './rect-accessors.js';
import {
  type BBox,
  countRealChars,
  usableLeftSegmentWidth,
} from './rect-label-utils.js';
import {
  rectLabelClipWrapper,
  rectLabelSpan,
  rectOverlayContainer,
} from './RectLabelOverlay.sty.js';
import { ELLIPSIS_CHARACTER } from '../../../../typography/utils.js';
import { getCanvasColor } from '../../../core/components/canvas/utils/canvas-color.js';
import { charsFontWidth12pixelsSize400Map } from '../../../core/components/text-ellipsis/chars-font-width-map.js';
import { truncateString } from '../../../core/components/text-ellipsis/truncate-string.js';
import { getColorByContrast } from '../../../core/utils/color-contrast.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import { useXYChartColorScales } from '../../hooks/useXYChartColorScales.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartTruncationMode } from '../../hooks/useXYChartTruncationMode.js';
import type { RectSeriesWithDatapoints } from '../../types/xy-chart-internals.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { relAccessor } from '../../utils/value-representation.js';

/** Minimum rect side (in pixels) below which text cannot be readably rendered. */
const RECT_LABEL_MIN_SIZE = 16;
/** Horizontal padding between the rect edge and the label text. */
const RECT_LABEL_PADDING = 4;
/**
 * Minimum count of non-ellipsis characters in the final truncated string.
 * Labels below this (e.g. `…`, `…A`) are dropped as visual noise.
 */
const RECT_LABEL_MIN_REAL_CHARS = 2;

interface RectLabelOverlayProps {
  axisIdToScales: AxisIdToScales;
  series: RectSeriesWithDatapoints;
  opacity?: number;
}

/**
 * Renders the HTML labels for a single rect series. Each datapoint's label is
 * placed inside the rect and clipped to the rect's bounds — the portion that
 * would overflow is hidden. This is the key difference from `BarLabelOverlay`,
 * whose labels are allowed to spill outside the bar toward the chart edge.
 *
 * The canvas paints rects in data order, so any rect later in the data appears
 * on top of earlier ones where they overlap. This overlay mirrors that by
 * iterating in reverse and, for each rect, clipping the label to the leftmost
 * horizontal segment that is not covered by an already-drawn (top-most) rect.
 * If a rect's leading edge is itself covered, or the remaining usable segment
 * cannot fit at least a couple of real characters, the label is dropped —
 * this keeps flame graphs readable when siblings share depth and time range.
 *
 * Rendered as a sibling of the `<Canvas>` element (not inside `RectDisplay`)
 * because HTML children of `<canvas>` are fallback content, not visible DOM.
 * See `RectLabelLayout.tsx` for the chart-level wrapper that iterates over
 * labeled rect series and mounts this component.
 */
export function RectLabelOverlay({
  axisIdToScales,
  series,
  opacity,
}: Readonly<RectLabelOverlayProps>) {
  const { datapointMetadataMap, axisIdToScaleDomain, seriesToMetadata } =
    useXYChartData();
  const colorScalesMap = useXYChartColorScales();
  const { truncationMode } = useXYChartTruncationMode();

  if (!series.hasLabels) {
    return null;
  }

  const colorScale = colorScalesMap.get(series.id);
  if (!colorScale) {
    return null;
  }

  const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
  const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
  const { xAxis, yAxis } = safeGet(seriesToMetadata, series);
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  const getX = xAccessor(xScale, xRelAccessor);
  const getY = yAccessor(yScale, yRelAccessor);
  const getW = widthAccessor(xScale, xRelAccessor);
  const getH = heightAccessor(yScale, yRelAccessor);
  const getFill = colorAccessor(colorScale, datapointMetadataMap);

  const containerStyle: CSSProperties | undefined =
    opacity !== undefined ? { opacity } : undefined;

  const drawnRects: BBox[] = [];
  const labels: ReactElement[] = [];

  for (let i = series.filteredData.length - 1; i >= 0; i--) {
    const datum = series.filteredData[i];
    const text = datum?.label;
    if (!datum || !text) {
      continue;
    }

    const x = getX(datum);
    const y = getY(datum);
    const w = getW(datum);
    const h = getH(datum);

    if (w < RECT_LABEL_MIN_SIZE || h < RECT_LABEL_MIN_SIZE) {
      continue;
    }

    const bbox: BBox = { x, y, w, h };
    const usableWidth = usableLeftSegmentWidth(bbox, drawnRects);
    drawnRects.push(bbox);

    if (usableWidth < RECT_LABEL_MIN_SIZE) {
      continue;
    }

    const availLength = usableWidth - 2 * RECT_LABEL_PADDING;
    if (availLength < RECT_LABEL_MIN_SIZE) {
      continue;
    }

    const truncated = truncateString(
      text,
      availLength,
      charsFontWidth12pixelsSize400Map,
      truncationMode,
    );

    if (
      truncated.includes(ELLIPSIS_CHARACTER) &&
      countRealChars(truncated) < RECT_LABEL_MIN_REAL_CHARS
    ) {
      continue;
    }

    const textColor = getColorByContrast(getCanvasColor(getFill(datum)));

    const wrapperStyle: CSSProperties = {
      left: x,
      top: y,
      width: usableWidth,
      height: h,
    };
    const innerStyle: CSSProperties = {
      left: RECT_LABEL_PADDING,
      top: h / 2,
      transform: 'translateY(-50%)',
      width: availLength,
      color: textColor,
    };

    labels.push(
      <div key={i} className={rectLabelClipWrapper} style={wrapperStyle}>
        <span className={rectLabelSpan} style={innerStyle}>
          {truncated}
        </span>
      </div>,
    );
  }

  return (
    <div className={rectOverlayContainer} style={containerStyle}>
      {labels}
    </div>
  );
}

RectLabelOverlay['displayName'] = 'RectLabelOverlay';
